export const usersFragment = gql`
  fragment UsersFragment on Integration {
    users {
      id
      role
      isBillingUser
      hidden
      user {
        id
        name
        email
        picture
        updatedAt
      }
    }
  }
`;

const invitesFragment = gql`
  fragment InvitesFragment on Integration {
    invites {
      id
      email
      role
      updatedAt
    }
  }
`;

const integrationFragment = gql`
  fragment IntegrationFragment on Integration {
    id
    externalId
    type
    description
    domain
    currency
    isDemo
    syncing
    isConnected
    connectionStatus
    connectionStatusReason
    healthStatus
    healthStatusReason
    healthQuickFix
    syncVisibility
    syncProgressPercent
    lastSyncedAt
    analyticsStatus {
      status
      lastRequestedAt
      lastCompletedAt
    }
    credentialStatus {
      kind
      expiresAt
      dataAccessExpiresAt
      verifiedAt
      expiresSoon
      awaitingSync
    }
    activeIssue {
      id
      kind
      status
      classification
      title
      description
      incidentSequence
      quickFixType
      lastFailedAt
      resolvedAt
    }
    syncProgress {
      windowEndAt
      completedThroughAt
      currentFromDate
      currentToDate
      currentExternalId
      currentRetries
      lastSucceededAt
      error
    }
    children {
      id
      parentId
      type
      description
      currency
      isDemo
      syncing
      isConnected
      connectionStatus
      connectionStatusReason
      healthStatus
      healthStatusReason
      healthQuickFix
      lastSyncedAt
      credentialStatus {
        kind
        expiresAt
        dataAccessExpiresAt
        verifiedAt
        expiresSoon
        awaitingSync
      }
      activeIssue {
        id
        kind
        status
        classification
        title
        description
        incidentSequence
        quickFixType
        lastFailedAt
        resolvedAt
      }
      syncVisibility
      settings
      users {
        id
        role
        hidden
        user {
          id
          name
          picture
        }
      }
    }
    settings
    deletedAt
    ...UsersFragment
    ...InvitesFragment
  }

  ${usersFragment}
  ${invitesFragment}
`;

export const INTEGRATIONS_QUERY = gql`
  query IntegrationsQuery($type: IntegrationType!) {
    integrations(type: $type) {
      ...IntegrationFragment
    }
  }

  ${integrationFragment}
`;

export const INTEGRATION_SOURCES_QUERY = gql`
  query IntegrationSourcesQuery($id: String!) {
    integrationSources(id: $id) {
      id
      name
      parentName
      currency
    }
  }
`;

export const SYNC_INTEGRATION_MUTATION = gql`
  mutation SyncIntegrationMutation($id: String!) {
    syncIntegration(id: $id) {
      ...IntegrationFragment
    }
  }

  ${integrationFragment}
`;

export const INTEGRATION_MUTATION = gql`
  mutation IntegrationMutation($id: String!, $input: UpdateIntegrationInput!) {
    updateIntegration(id: $id, input: $input) {
      ...IntegrationFragment
    }
  }

  ${integrationFragment}
`;

export const INTEGRATION_CALLBACK_MUTATION = gql`
  mutation IntegrationCallbackMutation($input: CreateIntegrationInput!) {
    integrationCallback(input: $input) {
      id
      description
      parentId
    }
  }
`;

export const INTEGRATION_URL_MUTATION = gql`
  mutation IntegrationUrlMutation($input: CreateIntegrationInput!) {
    integrationUrl(input: $input)
  }
`;

export const DELETE_INTEGRATION_MUTATION = gql`
  mutation DeleteIntegrationMutation($id: String!) {
    deleteIntegration(id: $id) {
      id
    }
  }
`;

export const LEAVE_INTEGRATION_MUTATION = gql`
  mutation LeaveIntegrationMutation($id: String!) {
    leaveIntegration(id: $id)
  }
`;

export const DEMO_STORE_STATUS_QUERY = gql`
  query DemoStoreStatusQuery {
    demoStoreStatus {
      joined
      dismissed
      integrationId
    }
  }
`;

export const JOIN_DEMO_STORE_MUTATION = gql`
  mutation JoinDemoStoreMutation {
    joinDemoStore {
      ...IntegrationFragment
    }
  }

  ${integrationFragment}
`;

export const INVITE_USER_TO_INTEGRATION_MUTATION = gql`
  mutation InviteUserToIntegrationMutation(
    $id: String!
    $email: String!
    $role: IntegrationUserRole!
  ) {
    inviteUserToIntegration(id: $id, email: $email, role: $role) {
      id
      ...InvitesFragment
    }
  }

  ${invitesFragment}
`;

export const REMOVE_USER_FROM_INTEGRATION_MUTATION = gql`
  mutation RemoveUserFromIntegrationMutation(
    $id: String!
    $userOrInviteId: String!
  ) {
    removeUserFromIntegration(id: $id, userOrInviteId: $userOrInviteId) {
      id
      ...UsersFragment
      ...InvitesFragment
    }
  }

  ${usersFragment}
  ${invitesFragment}
`;

export const UPDATE_USER_ROLE_IN_INTEGRATION_MUTATION = gql`
  mutation UpdateUserRoleInIntegrationMutation(
    $id: String!
    $userId: String!
    $role: IntegrationUserRole!
  ) {
    updateUserRoleInIntegration(id: $id, userId: $userId, role: $role) {
      id
      ...UsersFragment
      ...InvitesFragment
    }
  }

  ${usersFragment}
  ${invitesFragment}
`;
